import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type {
  ApplyFertWorkSyncInput,
  FertWorkSyncField,
  FertWorkSyncMissingAction,
  FertWorkSyncMissingReason,
  FertWorkSyncPreview,
  FertWorkSyncSireValues,
} from '@/services/fert-work';

const KEEP_BOTH_HALVES = 'keep';

function toRoman(value: number) {
  const ROMAN_NUMERALS: [number, string][] = [
    [10, 'X'],
    [9, 'IX'],
    [5, 'V'],
    [4, 'IV'],
    [1, 'I'],
  ];

  let remaining = value;
  let roman = '';
  for (const [amount, numeral] of ROMAN_NUMERALS) {
    while (remaining >= amount) {
      roman += numeral;
      remaining -= amount;
    }
  }
  return roman;
}

export type SyncUpdatesDialogProps = {
  preview: FertWorkSyncPreview | null;
  isApplying: boolean;
  notice?: string | null;
  onCancel: () => void;
  onApply: (input: ApplyFertWorkSyncInput) => void;
};

export function SyncUpdatesDialog({
  preview,
  isApplying,
  notice,
  onCancel,
  onApply,
}: SyncUpdatesDialogProps) {
  const { t } = useTranslation();

  const [splitChoices, setSplitChoices] = useState<Record<string, string>>({});
  const [missingChoices, setMissingChoices] = useState<
    Record<string, FertWorkSyncMissingAction>
  >({});
  const [skippedUpdateIds, setSkippedUpdateIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [skippedNewRecordIds, setSkippedNewRecordIds] = useState<Set<string>>(
    () => new Set(),
  );

  const toggle = (
    setter: (update: (previous: Set<string>) => Set<string>) => void,
    id: string,
  ) =>
    setter((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });

  if (!preview) {
    return null;
  }

  const fieldLabels: Record<FertWorkSyncField, string> = {
    OPU_SITE: t('fertWork.opuSite', 'OPU Site'),
    MAT_TIME: t('fertWork.matTime', 'Mat Time'),
    TOTAL_OOCYTES: t('fertWork.totalOocytes', 'Total Oocytes'),
    DONOR_LOCATION: t('fertWork.donorLocation', 'Donor Location'),
    DONOR: t('fertWork.donor', 'Donor'),
    DONOR_BREED: t('fertWork.donorBreed', 'Donor Breed'),
    SIRE: t('fertWork.sire', 'Sire'),
    SEMEN_PULL_SIRE_NOTE: t(
      'fertWork.semenPullSireNote',
      'Semen Pull Sire Note',
    ),
    SIRE_BREED: t('fertWork.sireBreed', 'Sire Breed'),
    COLLECTION_CODE: t('fertWork.collectionCode', 'Collection Code'),
    SEMEN_TYPE: t('fertWork.semenType', 'Semen Type'),
    SEMEN_COMMENTS: t('fertWork.semenComments', 'Semen Comments'),
    SEMEN_OWNER: t('fertWork.semenOwner', 'Semen Owner'),
    ACCOUNT: t('fertWork.account', 'Account'),
    SHIP_LABEL: t('fertWork.shipLabel', 'Ship Label'),
    EXPORT: t('fertWork.export', 'Export'),
    SPLIT: t('fertWork.split', 'Split'),
    SPLIT_OOCYTES: t('fertWork.splitOocytes', 'Split Oocytes'),
  };

  const missingReasons: Record<FertWorkSyncMissingReason, string> = {
    DELETED: t(
      'fertWork.syncUpdates.reasonDeleted',
      'The donor schedule was deleted.',
    ),
    CANCELLED: t(
      'fertWork.syncUpdates.reasonCancelled',
      'The donor schedule was cancelled.',
    ),
    DATE_CHANGED: t(
      'fertWork.syncUpdates.reasonDateChanged',
      'The donor schedule was moved to a different date.',
    ),
    SITE_CHANGED: t(
      'fertWork.syncUpdates.reasonSiteChanged',
      'The donor schedule was moved to a different fertilization site.',
    ),
    NO_LONGER_ELIGIBLE: t(
      'fertWork.syncUpdates.reasonNoLongerEligible',
      'The donor schedule no longer matches this session, for example because its fert plan, breed or account changed.',
    ),
  };

  const emptyValue = t('fertWork.syncUpdates.emptyValue', '(empty)');
  const formatSire = (values: FertWorkSyncSireValues) =>
    [values.sireName, values.semenType, values.collectionCode]
      .filter((value) => !!value)
      .join(' · ') || emptyValue;

  const decisionCount =
    preview.splitSireDecisions.length + preview.missingScheduleDecisions.length;
  const hasAllDecisions =
    preview.splitSireDecisions.every(
      (decision) => !!splitChoices[decision.decisionId],
    ) &&
    preview.missingScheduleDecisions.every(
      (decision) => !!missingChoices[decision.decisionId],
    );

  const handleApply = () => {
    onApply({
      previewToken: preview.previewToken,
      splitSireChoices: preview.splitSireDecisions.map((decision) => {
        const choice = splitChoices[decision.decisionId];
        return {
          decisionId: decision.decisionId,
          targetDetailId:
            !choice || choice === KEEP_BOTH_HALVES ? null : choice,
        };
      }),
      missingScheduleChoices: preview.missingScheduleDecisions.map(
        (decision) => ({
          decisionId: decision.decisionId,
          action: missingChoices[decision.decisionId],
        }),
      ),
      skippedUpdateDetailIds: [...skippedUpdateIds],
      skippedNewRecordIds: [...skippedNewRecordIds],
    });
  };

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && !isApplying) {
          onCancel();
        }
      }}
    >
      <DialogContent className="md:max-w-3xl">
        <DialogHeader>
          <DialogTitle>
            {t('fertWork.syncUpdates.title', 'Check for Updated Records')}
          </DialogTitle>
          <DialogDescription>
            {t(
              'fertWork.syncUpdates.description',
              'Review the changes from the donor schedules. Nothing is saved until you apply them, and your session progress is kept.',
            )}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex max-h-[65vh] flex-col gap-5 overflow-y-auto">
          {notice && (
            <div
              role="alert"
              className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900"
            >
              {notice}
            </div>
          )}

          <p className="text-sm text-gray-700">
            {t(
              'fertWork.syncUpdates.summary',
              '{newCount, plural, one {# new record} other {# new records}}, {updateCount, plural, one {# updated record} other {# updated records}}, {decisionCount, plural, one {# item needs} other {# items need}} your decision.',
              {
                newCount: preview.newRecords.length,
                updateCount: preview.updates.length,
                decisionCount,
              },
            )}
          </p>

          {decisionCount > 0 && (
            <section className="flex flex-col gap-3">
              <h3 className="text-sm font-semibold">
                {t('fertWork.syncUpdates.needsDecision', 'Needs your decision')}
              </h3>

              {preview.splitSireDecisions.map((decision) => {
                const matchingIndex = decision.halves.findIndex(
                  (half) => half.detailId === decision.matchingHalfDetailId,
                );
                const groupName = `split-${decision.decisionId}`;
                return (
                  <fieldset
                    key={decision.decisionId}
                    className="flex flex-col gap-2 rounded-md border border-gray-200 p-3"
                  >
                    <legend className="px-1 text-sm font-medium">
                      {t(
                        'fertWork.syncUpdates.splitSireTitle',
                        '{donor}: new sire on a split donor',
                        { donor: decision.donorName ?? emptyValue },
                      )}
                    </legend>
                    <p className="text-sm">
                      {t(
                        'fertWork.syncUpdates.splitSireSource',
                        'The donor schedule now uses {sire}. Choose which half gets it.',
                        { sire: formatSire(decision.source) },
                      )}
                    </p>
                    {matchingIndex >= 0 && (
                      <p className="flex items-start gap-2 rounded bg-amber-50 px-2 py-1 text-sm text-amber-900">
                        <i
                          className="pi pi-info-circle mt-0.5"
                          aria-hidden="true"
                        />
                        {t(
                          'fertWork.syncUpdates.matchingHalf',
                          'Half {half} already uses this sire, semen type and collection code. The split may no longer be needed.',
                          { half: toRoman(matchingIndex + 1) },
                        )}
                      </p>
                    )}
                    {decision.halves.map((half, index) => (
                      <label
                        key={half.detailId}
                        className="flex items-center gap-2 text-sm"
                      >
                        <input
                          type="radio"
                          name={groupName}
                          value={half.detailId}
                          checked={
                            splitChoices[decision.decisionId] === half.detailId
                          }
                          onChange={() =>
                            setSplitChoices((previous) => ({
                              ...previous,
                              [decision.decisionId]: half.detailId,
                            }))
                          }
                        />
                        {half.isOriginal
                          ? t(
                              'fertWork.syncUpdates.replaceOriginalHalf',
                              'Replace half {half} (from the donor schedule): {current}',
                              {
                                half: toRoman(index + 1),
                                current: formatSire(half.current),
                              },
                            )
                          : t(
                              'fertWork.syncUpdates.replaceHalf',
                              'Replace half {half}: {current}',
                              {
                                half: toRoman(index + 1),
                                current: formatSire(half.current),
                              },
                            )}
                      </label>
                    ))}
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name={groupName}
                        value={KEEP_BOTH_HALVES}
                        checked={
                          splitChoices[decision.decisionId] === KEEP_BOTH_HALVES
                        }
                        onChange={() =>
                          setSplitChoices((previous) => ({
                            ...previous,
                            [decision.decisionId]: KEEP_BOTH_HALVES,
                          }))
                        }
                      />
                      {t(
                        'fertWork.syncUpdates.keepBothHalves',
                        'Keep both halves as they are',
                      )}
                    </label>
                  </fieldset>
                );
              })}

              {preview.missingScheduleDecisions.map((decision) => {
                const groupName = `missing-${decision.decisionId}`;
                return (
                  <fieldset
                    key={decision.decisionId}
                    className="flex flex-col gap-2 rounded-md border border-gray-200 p-3"
                  >
                    <legend className="px-1 text-sm font-medium">
                      {decision.rows
                        .map((row) =>
                          [row.donorName, row.sireName]
                            .filter((value) => !!value)
                            .join(' · '),
                        )
                        .join(', ')}
                    </legend>
                    <p className="text-sm">{missingReasons[decision.reason]}</p>
                    {(decision.isOnRuns || decision.isOnLabels) && (
                      <p className="flex items-start gap-2 rounded bg-amber-50 px-2 py-1 text-sm text-amber-900">
                        <i
                          className="pi pi-exclamation-triangle mt-0.5"
                          aria-hidden="true"
                        />
                        {t(
                          'fertWork.syncUpdates.onRunsOrLabels',
                          'This record is on generated runs or labels. If you remove it, regenerate them afterwards.',
                        )}
                      </p>
                    )}
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name={groupName}
                        value="REMOVE"
                        checked={
                          missingChoices[decision.decisionId] === 'REMOVE'
                        }
                        onChange={() =>
                          setMissingChoices((previous) => ({
                            ...previous,
                            [decision.decisionId]: 'REMOVE',
                          }))
                        }
                      />
                      {t(
                        'fertWork.syncUpdates.missingRemove',
                        'Remove: take it out of this session',
                      )}
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name={groupName}
                        value="KEEP"
                        checked={missingChoices[decision.decisionId] === 'KEEP'}
                        onChange={() =>
                          setMissingChoices((previous) => ({
                            ...previous,
                            [decision.decisionId]: 'KEEP',
                          }))
                        }
                      />
                      {t(
                        'fertWork.syncUpdates.missingKeep',
                        'Keep: leave it in this session and ask again next time',
                      )}
                    </label>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name={groupName}
                        value="ORPHAN"
                        checked={
                          missingChoices[decision.decisionId] === 'ORPHAN'
                        }
                        onChange={() =>
                          setMissingChoices((previous) => ({
                            ...previous,
                            [decision.decisionId]: 'ORPHAN',
                          }))
                        }
                      />
                      {t(
                        'fertWork.syncUpdates.missingOrphan',
                        'Orphan: leave it in this session and never sync it again, even if the donor schedule comes back',
                      )}
                    </label>
                  </fieldset>
                );
              })}
            </section>
          )}

          {preview.updates.length > 0 && (
            <section className="flex flex-col gap-3">
              <h3 className="text-sm font-semibold">
                {t('fertWork.syncUpdates.updates', 'Updates')}
              </h3>
              <p className="text-xs text-gray-600">
                {t(
                  'fertWork.syncUpdates.uncheckHint',
                  'Uncheck anything you don’t want to apply now. It will be listed again the next time you check for updated records.',
                )}
              </p>
              {preview.updates.map((update) => (
                <div
                  key={update.detailId}
                  className="rounded-md border border-gray-200"
                >
                  <label className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-3 py-1.5 text-sm font-medium">
                    <input
                      type="checkbox"
                      checked={!skippedUpdateIds.has(update.detailId)}
                      onChange={() =>
                        toggle(setSkippedUpdateIds, update.detailId)
                      }
                    />
                    {[update.donorName, update.sireName]
                      .filter((value) => !!value)
                      .join(' · ')}
                  </label>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-500">
                        <th className="px-3 py-1 font-medium">
                          {t('fertWork.syncUpdates.field', 'Field')}
                        </th>
                        <th className="px-3 py-1 font-medium">
                          {t('fertWork.syncUpdates.currentValue', 'Current')}
                        </th>
                        <th className="px-3 py-1 font-medium">
                          {t('fertWork.syncUpdates.newValue', 'New')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {update.changes.map((change) => (
                        <tr key={change.field}>
                          <td className="px-3 py-1">
                            {fieldLabels[change.field] ?? change.field}
                          </td>
                          <td className="px-3 py-1 text-gray-600 line-through">
                            {change.oldValue || emptyValue}
                          </td>
                          <td className="px-3 py-1 font-medium">
                            {change.newValue || emptyValue}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </section>
          )}

          {preview.newRecords.length > 0 && (
            <section className="flex flex-col gap-2">
              <h3 className="text-sm font-semibold">
                {t('fertWork.syncUpdates.newRecords', 'New records')}
              </h3>
              <ul className="flex flex-col gap-1 text-sm">
                {preview.newRecords.map((record) => (
                  <li key={record.sourceRecordId}>
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={
                          !skippedNewRecordIds.has(record.sourceRecordId)
                        }
                        onChange={() =>
                          toggle(setSkippedNewRecordIds, record.sourceRecordId)
                        }
                      />
                      {t(
                        'fertWork.syncUpdates.newRecordLine',
                        '{donor} · {sire} · {oocytes} oocytes',
                        {
                          donor: record.donorName || emptyValue,
                          sire: formatSire(record),
                          oocytes: record.totalOocytes,
                        },
                      )}
                    </label>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {preview.unlinkedRecordCount > 0 && (
            <p className="text-xs text-gray-600">
              {t(
                'fertWork.syncUpdates.unlinkedRecords',
                '{count, plural, one {# older record isn’t} other {# older records aren’t}} linked to a donor schedule and won’t be updated.',
                { count: preview.unlinkedRecordCount },
              )}
            </p>
          )}
        </DialogBody>

        <DialogFooter>
          {!hasAllDecisions && (
            <span className="mr-auto self-center text-sm text-gray-600">
              {t(
                'fertWork.syncUpdates.chooseAll',
                'Choose an option for every item that needs your decision.',
              )}
            </span>
          )}
          <Button variant="ghost" onClick={onCancel} disabled={isApplying}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleApply}
            disabled={!hasAllDecisions || isApplying}
          >
            {isApplying && (
              <i className="pi pi-spin pi-spinner" aria-hidden="true" />
            )}
            {t('fertWork.syncUpdates.apply', 'Apply updates')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
